import React, { useState } from "react";
import { useIntl } from "react-intl";

import BinIcon from "@assets/icons/rounded/bin.svg";
import { ClickableComponent } from "@app/core";
import { SelectComponent } from "@app/core/select";
import { ReactSelect } from "@app/core/react-select/react-select";
import { FilterItem } from "@app/api/core/filter/filter-item";
import { mapToFilterItem } from "@app/api/core/filter/map-filter-item";
import { getFilters } from "@app/modules/deal-detail/components/api-calls";
import { statusDealOptions, IDealEditOptions, BulkLabelMode, bulkLabelModeOptions } from "@app/constants/deal";

import styles from "./deal-overview-options-bar-component.module.scss";

export interface IDealOverviewOptionsBarProps {
  isSelected: number[];
  onEdit: (changes: IDealEditOptions) => void;
  onRemove: () => void;
}

// Same source as the label field in the deal form: filters of type 2 (labels).
const LABEL_FILTER_TYPE_IDS = "2";

const DealOverviewOptionsBar = (props: IDealOverviewOptionsBarProps) => {
  const [selectedStatus, setSelectedStatus] = useState<number | null>(0);
  const [labelMode, setLabelMode] = useState<BulkLabelMode>(BulkLabelMode.Unchanged);
  const [selectedLabel, setSelectedLabel] = useState<FilterItem | null>(null);
  const intl = useIntl();

  const labelModeOptions = bulkLabelModeOptions.map((option) => ({
    ...option,
    name: intl.formatMessage({ id: option.name })
  }));

  const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(Number(e.target.value));
  };

  const onLabelModeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLabelMode(Number(e.target.value) as BulkLabelMode);
  };

  const loadLabels = async (inputValue: string, callback: (options: FilterItem[]) => void) => {
    const labels = await getFilters(inputValue, LABEL_FILTER_TYPE_IDS);
    callback(labels ? labels.map(mapToFilterItem) : []);
  };

  const getFeaturedFilterId = (): number | null | undefined => {
    if (labelMode === BulkLabelMode.Set) {
      return selectedLabel ? Number(selectedLabel.value) : undefined;
    }
    return labelMode === BulkLabelMode.Clear ? null : undefined;
  };

  const featuredFilterId = getFeaturedFilterId();
  const hasChanges = Boolean(selectedStatus) || featuredFilterId !== undefined;

  return (
    <div className={styles.wrapper}>
      <div className={styles.editSection}>
        <SelectComponent height={36} onChange={onStatusChange} options={statusDealOptions} value={selectedStatus} />
        <SelectComponent
          id="bulkLabelMode"
          height={36}
          onChange={onLabelModeChange}
          options={labelModeOptions}
          value={labelMode}
        />
        {labelMode === BulkLabelMode.Set && (
          <div className={styles.labelSelect}>
            <ReactSelect
              id="bulkFeaturedFilter"
              aria-label={intl.formatMessage({ id: "dealForm.input.featuredFilter.label" })}
              isAsync
              isClearable
              value={selectedLabel}
              loadOptions={loadLabels}
              onChange={(value) => setSelectedLabel(value as FilterItem | null)}
            />
          </div>
        )}
        <ClickableComponent
          buttonType="submit"
          title={intl.formatMessage({ id: "dealOverviewOptionsBarComponent.button.editDeals" })}
          height={36}
          onClick={() => props.onEdit({ status: selectedStatus, featuredFilterId })}
          disabled={props.isSelected.length === 0 || !hasChanges}
        />
      </div>
      <div className={styles.removeSection}>
        <div className={styles.amount}>
          {intl.formatMessage(
            { id: "dealOverviewOptionsBarComponent.selectedDeals" },
            { amount: props.isSelected.length }
          )}
        </div>
        <ClickableComponent
          buttonType="submit"
          title={intl.formatMessage({ id: "dealOverviewOptionsBarComponent.button.deleteDeals" })}
          height={36}
          onClick={props.onRemove}
          icon={BinIcon}
          iconSize="18px"
          iconPosition="left"
          iconStyle="filled"
          variant="tertiary"
          disabled={props.isSelected.length === 0}
        />
      </div>
    </div>
  );
};

export { DealOverviewOptionsBar };
